/**
 * GenerationSteps
 *
 * Two-panel generation progress UI.
 * Left: ActiveStepCard — large icon, live message, elapsed timer, pending steps.
 * Right: HorizontalStepTrack — pill strip with animated connector lines.
 *
 * Subscribes to WebSocket for real-time progress events.
 * Detects stale tasks (no activity for 2 min) and WebSocket disconnections.
 */

import { useEffect, useState, useCallback, useRef } from 'react'
import { XCircle, WifiOff, Clock } from 'lucide-react'
import {
  Loader2,
  Search,
  FileEdit,
  PenLine,
  ShieldCheck,
  MessageSquare,
  Image,
  CheckCircle2,
} from 'lucide-react'
import { getWebSocketBaseUrl } from '../../lib/api'
import { HorizontalStepTrack } from './HorizontalStepTrack'
import { ActiveStepCard } from './ActiveStepCard'
import { ProgressiveContentArea } from './ProgressiveContentArea'

export interface StepDef {
  id: string
  phaseKey: string
  label: string
  icon: React.ReactNode
}

export interface PhaseEvent {
  message: string | null
  completedAt: number
  data: Record<string, unknown> | null
}

export const PIPELINE_STEPS: StepDef[] = [
  { id: 'init', phaseKey: 'initializing', label: 'Inicializando', icon: <Loader2 className="w-4 h-4" /> },
  { id: 'research', phaseKey: 'researching', label: 'Investigación', icon: <Search className="w-4 h-4" /> },
  { id: 'plan', phaseKey: 'planning', label: 'Planificación', icon: <FileEdit className="w-4 h-4" /> },
  { id: 'generate', phaseKey: 'generating', label: 'Generando contenido', icon: <PenLine className="w-4 h-4" /> },
  { id: 'validation', phaseKey: 'validation', label: 'Validación', icon: <ShieldCheck className="w-4 h-4" /> },
  { id: 'critic', phaseKey: 'critiquing', label: 'Crítica', icon: <MessageSquare className="w-4 h-4" /> },
  { id: 'visual', phaseKey: 'visual_generation', label: 'Generando visuales', icon: <Image className="w-4 h-4" /> },
  { id: 'done', phaseKey: 'completed', label: 'Finalizando', icon: <CheckCircle2 className="w-4 h-4" /> },
]

// How long without any WS activity before showing a stale warning (ms)
const STALE_THRESHOLD_MS = 2 * 60 * 1000
// How often to check for stale state (ms)
const STALE_CHECK_INTERVAL_MS = 30_000

interface GenerationStepsProps {
  taskId: string
  isGenerating: boolean
  onCancel?: () => void
}

export function GenerationSteps({ taskId, isGenerating, onCancel }: GenerationStepsProps) {
  const [phaseEvents, setPhaseEvents] = useState<Record<string, PhaseEvent>>({})
  const [activePhase, setActivePhase] = useState<string | null>(null)
  const [activeStartedAt, setActiveStartedAt] = useState<number>(Date.now())
  const [streamBuffer, setStreamBuffer] = useState<string>('')
  const [wsConnected, setWsConnected] = useState<boolean>(true)
  const [isStale, setIsStale] = useState<boolean>(false)
  const [isCancelled, setIsCancelled] = useState<boolean>(false)

  // Tracks the last time any WS message was received
  const lastActivityAt = useRef<number>(Date.now())
  // Tracks whether the WS ever successfully delivered at least one message
  const everConnected = useRef<boolean>(false)

  // Ref keeps the current activePhase readable inside WS callbacks without re-binding
  const activePhaseRef = useRef<string | null>(null)
  activePhaseRef.current = activePhase

  const handleMessage = useCallback(
    (event: {
      type?: string
      phase?: string
      message?: string | null
      data?: Record<string, unknown> | null
    }) => {
      lastActivityAt.current = Date.now()
      everConnected.current = true
      setIsStale(false)

      // Handle task cancellation
      if (event.type === 'cancelled') {
        setIsCancelled(true)
        setActivePhase(null)
        return
      }

      if (event.type !== 'progress' || !event.phase) return

      const phase = event.phase

      // Accumulate streaming tokens for the generating phase
      if (
        phase === 'generating' &&
        event.data?.stream === true &&
        typeof event.data.token === 'string'
      ) {
        setStreamBuffer((prev) => prev + (event.data!.token as string))
        return
      }

      const prevPhase = activePhaseRef.current

      // Record the completed phase with its own message snapshot (not the incoming event's message)
      if (prevPhase && prevPhase !== phase) {
        setPhaseEvents((prev) => ({
          ...prev,
          [prevPhase]: {
            message: prev[prevPhase]?.message ?? null,
            completedAt: Date.now(),
            data: prev[prevPhase]?.data ?? null,
          },
        }))
      }

      // Capture live message for the active phase
      if (event.message) {
        setPhaseEvents((prev) => ({
          ...prev,
          [phase]: {
            message: event.message ?? null,
            completedAt: prev[phase]?.completedAt ?? 0,
            data: event.data ?? prev[phase]?.data ?? null,
          },
        }))
      }

      setActivePhase((prev) => {
        if (prev !== phase) {
          setActiveStartedAt(Date.now())
          if (phase === 'generating') {
            setStreamBuffer('')
          }
        }
        return phase
      })
    },
    [],
  )

  // WebSocket lifecycle
  useEffect(() => {
    if (!taskId || !isGenerating) return

    const wsUrl = `${getWebSocketBaseUrl()}/ws/generation/${taskId}`
    let ws: WebSocket | null = null
    let closed = false

    try {
      ws = new WebSocket(wsUrl)
      ws.onmessage = (event) => {
        if (closed) return
        try {
          const parsed = JSON.parse(event.data) as {
            type?: string
            phase?: string
            message?: string | null
            data?: Record<string, unknown> | null
          }
          handleMessage(parsed)
        } catch {
          // ignore parse errors
        }
      }
      ws.onerror = () => {
        if (!closed) setWsConnected(false)
      }
      ws.onclose = () => {
        closed = true
        setWsConnected(false)
      }
      ws.onopen = () => {
        setWsConnected(true)
      }
    } catch {
      setWsConnected(false)
    }

    return () => {
      closed = true
      if (ws && ws.readyState === WebSocket.OPEN) ws.close()
    }
  }, [taskId, isGenerating, handleMessage])

  // Stale detection: check every 30s whether we've had recent WS activity
  useEffect(() => {
    if (!isGenerating) return

    const interval = setInterval(() => {
      const elapsed = Date.now() - lastActivityAt.current
      if (elapsed > STALE_THRESHOLD_MS) {
        setIsStale(true)
      }
    }, STALE_CHECK_INTERVAL_MS)

    return () => clearInterval(interval)
  }, [isGenerating])

  const activeStep = PIPELINE_STEPS.find((s) => s.phaseKey === activePhase) ?? null
  // Show live message even while the phase is still active
  const activeMessage = activePhase ? (phaseEvents[activePhase]?.message ?? null) : null

  const pendingSteps = PIPELINE_STEPS.filter(
    (s) => s.phaseKey !== activePhase && !(s.phaseKey in phaseEvents),
  )

  return (
    <div className="flex-1 flex flex-col min-h-0 px-4 py-6 gap-4">
      {/* Stale warning banner */}
      {isStale && !isCancelled && (
        <div className="flex items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <Clock className="w-4 h-4 shrink-0" />
          <span className="flex-1">
            La generacion esta tardando mas de lo esperado. El proceso sigue activo — puedes esperar o cancelar la tarea.
          </span>
          {onCancel && (
            <button
              onClick={onCancel}
              className="text-amber-700 underline underline-offset-2 hover:text-amber-900 shrink-0"
            >
              Cancelar
            </button>
          )}
        </div>
      )}

      {/* WS disconnection badge */}
      {!wsConnected && everConnected.current && !isCancelled && (
        <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
          <WifiOff className="w-3.5 h-3.5 shrink-0" />
          Sin conexion en tiempo real — el estado se actualiza cada 5 s
        </div>
      )}

      {/* Cancelled notice */}
      {isCancelled && (
        <div className="flex items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          <XCircle className="w-4 h-4 shrink-0" />
          La tarea fue cancelada.
        </div>
      )}

      {/* Step track strip */}
      <div className="h-12 flex items-center px-2">
        <HorizontalStepTrack
          steps={PIPELINE_STEPS}
          activePhase={activePhase}
          phaseEvents={phaseEvents}
        />
      </div>

      {/* Two-panel area */}
      <div className="flex flex-1 gap-4 min-h-0">
        {/* Left panel — active step detail */}
        <div className="w-64 shrink-0 rounded-xl border border-slate-100 bg-white shadow-sm overflow-hidden">
          <ActiveStepCard
            step={activeStep}
            message={activeMessage}
            startedAt={activeStartedAt}
            pendingSteps={pendingSteps}
          />
        </div>

        {/* Right panel — progressive step output cards */}
        <ProgressiveContentArea
          phaseEvents={phaseEvents}
          activePhase={activePhase}
          streamBuffer={streamBuffer}
        />
      </div>

      {/* Cancel button */}
      {onCancel && !isCancelled && (
        <div className="flex justify-center pt-2">
          <button
            onClick={onCancel}
            className="btn btn-secondary flex items-center gap-2 text-red-600 hover:text-red-700 hover:bg-red-50"
          >
            <XCircle className="w-4 h-4" />
            Cancelar tarea
          </button>
        </div>
      )}
    </div>
  )
}
